/**
 * The neural canvas.
 *
 * The grammar, stated once so it can be checked against the code:
 *
 *   strand thickness  →  confidence in the inference
 *   strand colour     →  what kind of move it is (support / tension / gap / insight)
 *   strand taper      →  direction of derivation; thick at the premise, thin at the claim
 *   strand drained grey →  a dead end, reasoned to exhaustion
 *   small closed ring   →  where that dead end was observed to stop
 *   node size         →  novelty and confidence, summed, novelty weighted heavier
 *                        (under the key bar's evidence weighting the two numbers
 *                        are what the records say instead — `core/weight.ts`;
 *                        nothing here changes, the map handed in does)
 *   node glow         →  novelty alone
 *                        (a claim you rewrote has no ratings: middle size, no glow,
 *                         a middle-weight strand — `drawnRatingsOf`)
 *   filled disc       →  an established move
 *   hollow ring       →  an open question
 *   crossed disc      →  a contradiction
 *   notched disc      →  an objection below it stood, in reasoning
 *   haloed disc       →  an insight or synthesis
 *   haloed hollow ring→  a conjecture nothing outside the model has tested yet
 *   travelling light  →  signal on a live path
 *   outward chevrons  →  a branch you have folded away
 *
 * And, for what has been checked from outside the graph:
 *
 *   magenta struck disc  →  a source, or a result, says otherwise
 *   crisp edge at the rim →  this claim was checked against sources
 *   rim broken open       →  …and a work that check cites has been retracted
 *   accent bar on strand  →  a result you recorded sits on this node
 *   split strand          →  three lenses read this node differently
 *   detached strand       →  reasoned before a result or newer source record above it
 *   dashed outward stub   →  fused with a branch in another exploration
 *
 * Nothing on this canvas is decorative. If a mark is drawn, it is because a
 * field in the data changed.
 */

import type { Filters, ReasonNode, Session, Viewport } from '../core/types'
import {
  channelOf,
  checkedAgainstSources,
  hollowGlyph,
  kindChannelOf,
  liveChannelOf,
} from '../core/types'
import { drawnGlowOf, drawnRatingsOf, writtenAt } from '../core/rewrite'
import { panelSplit } from '../core/panel'
import { ringRadius } from '../core/layout'
import { BASE } from '../core/tempo'
import { motion } from '../core/motion'
import { pulseLevel, pulses } from '../core/pulse'
import { taintMap } from '../core/taint'
import type { Taint } from '../core/taint'
import { PRIOR_VERSION_INK, evidenceStaleMap, priorVersionIds } from '../core/rebase'
import type { EvidenceStale } from '../core/rebase'
import { growth } from './growth'
import { addPoly } from './path'
import { groundAfterResize } from './ground'
import { citesRetracted, traceBrokenRim } from './rim'
import { traceNotched } from './notch'
import { stoodAgainst } from '../core/endings'
import { SEED_RIM_MAX, seedRimFor, seedRingCap } from './seedCaption'
import { screen } from './screen'
import type { Palette, RGB } from '../core/palette'
import { hot, hoverRingAlpha, lift, mix, rgba, washed } from '../core/palette'
import {
  type Pt,
  TAU,
  arcTable,
  clamp,
  distToPolyline,
  easeOutBack,
  easeOutCubic,
  hash01,
  offsetPath,
  pointAtArc,
  ribbon,
  saltatory,
  smoothstep,
  strandPath,
  tracePoly,
} from '../core/geometry'

export interface FrameInput {
  session: Session
  childrenOf: Map<string, string[]>
  visible: Set<string>
  /** Per collapsed node, how many marks one unfold reveals. Zero means the fold
   *  is inert — its subtree was pruned, or the filters hide all of it. */
  folded: Map<string, number>
  focus: Set<string> | null
  /** The transient path the coach's current cue is asking the reader to hold.
   *  Separate from `focus`: focus belongs to the user and survives the guide. */
  guideKeep: Set<string> | null
  /** The cue's exact anchor. The DOM label marks it; the canvas only uses the
   *  keep-set so it never invents a new semantic glyph. */
  guideTargetId: string | null
  selectedId: string | null
  hoverId: string | null
  activeTargets: string[]
  filters: Filters
  view: Viewport
  palette: Palette
  busy: boolean
  rev: number
  /** Growth speed multiplier. Higher is faster. */
  tempo: number
  /** Non-null while scrubbing or replaying the timeline. */
  playhead: number | null
  /** When the playhead last moved — replay growth is derived from this. */
  playheadAt: number
  /** The footer's Rings switch (D369): the faint depth rings behind the graph. */
  rings: boolean
}

interface EdgeGeom {
  pts: Pt[]
  table: number[]
  rev: number
  /** The settled strand's polygons, kept as paths. See `strandPath`. */
  core?: CachedPath
  halo?: CachedPath
}

/**
 * A strand polygon and every input it was tessellated from. A mismatch on any
 * field rebuilds it; a match fills the path as it stands. The fields are
 * compared one by one rather than through a key string so a hit allocates
 * nothing — there are a few hundred of these per frame.
 */
interface CachedPath {
  path: Path2D
  /** The geometry key the points came from — `EdgeGeom.rev`. */
  rev: number
  /** `zoomBucket(v.k)`: the width floor is a screen-pixel quantity. */
  kb: number
  stale: boolean
  wFull: number
  wTip: number
  /** 0 or 1 once a parting has settled; a strand mid-parting is not cached. */
  split: number
  /** The halo's spread multiplier, quantised; 0 for the core. */
  spread: number
}

/**
 * Zoom, in 10 % steps, for the path cache.
 *
 * The only place `v.k` enters a strand's polygon is the width floor
 * `0.18 / v.k` — a minimum of 0.18 SCREEN pixels of half-width, so the thinnest
 * tip is still a hairline — and the split strand's screen-pixel parting. Both
 * are sub-pixel quantities: a 10 % error in either is a few hundredths of a
 * pixel, and buckets this coarse mean a zoom gesture rebuilds each strand every
 * few frames rather than every frame, which is what it used to cost anyway.
 */
function zoomBucket(k: number): number {
  return Math.round(Math.log(k) * 10)
}

/**
 * Growth choreography, in the order a real neurite does it.
 *
 *   PROBE        filopodia fan out from the parent and sample several
 *                directions; the losers retract. This is the moment of
 *                indecision, and it is what makes the graph feel like it is
 *                *deciding* rather than being drawn.
 *   EXTEND       a growth cone crawls the path — in surges, not at constant
 *                speed — trailing a thin, pale, immature shaft.
 *   ARRIVE       the cone reaches its target, flattens into a bouton, and the
 *                node springs in.
 *   CONSOLIDATE  a brightening wave sweeps outward from the parent and the
 *                shaft thickens to its full confidence width behind it. The
 *                connection stops being tentative and becomes structure.
 *
 * Every phase is a pure function of (now − birth), so the whole thing scrubs
 * correctly on the timeline and survives a paused tab.
 */
const PROBE_MS = BASE.probe
const EXTEND_MS = BASE.extend
const CONSOLIDATE_MS = BASE.consolidate
const ARRIVE_MS = BASE.arrive
/** When the node itself appears — the instant the cone touches down. */
const NODE_AT = PROBE_MS + EXTEND_MS
const GROW_TOTAL = NODE_AT + CONSOLIDATE_MS

/** How long a fold takes to retract, or a release to fan back out. */
const FOLD_MS = 520
/** How long a strand takes to part when a panel comes back divided. */
const SPLIT_MS = 620
/**
 * How far apart the two halves of a split strand run, in SCREEN pixels.
 *
 * Screen pixels rather than world units for the same reason the filopodia are:
 * a separation specified in world units is invisible at the zoom you actually
 * read the graph at, and absurd at the zoom you inspect one node at.
 */
const SPLIT_PX = 3.4
/**
 * Below this zoom a split strand collapses back to one line.
 *
 * Two strands 3.4px apart at k = 0.25 are one strand of double weight, which reads
 * as *more* confidence — the opposite of what a split means. Better to say nothing
 * than to say the reverse.
 */
const SPLIT_MIN_K = 0.3
/** Where a strand detaches from its parent when it predates a recorded result. */
const DETACH = 0.14
/** One trip of the "working on this branch" signal, root → active node. */
const DISPATCH_MS = BASE.dispatch
/**
 * The least clear field around the seed, world px. Strands are clipped out of
 * an ellipse round the seed's caption (`seedClear`), at least this big, so the
 * title sits isolated at the centre and the branches stem from around it.
 * First-ring captions are pushed outside it too (`screen.orbR`).
 */
const ROOT_CLEAR = 170

export class Renderer {
  private ctx: CanvasRenderingContext2D
  private dpr = 1
  private w = 0
  private h = 0
  private geom = new Map<string, EdgeGeom>()
  /** Node halo gradients, built once per (spread, hue, alpha) at the origin and
   *  drawn under a translate. See `haloGradient`. */
  private gradients = new Map<string, CanvasGradient>()
  /** Last frame's camera, revision, timeline and size — what `screen.frame` is
   *  bumped against. See `screen.ts`. */
  private lastK = NaN
  private lastX = NaN
  private lastY = NaN
  private lastRev = -1
  private lastPlayhead: number | null = null
  private lastPlayheadAt = NaN
  private lastW = 0
  private lastH = 0
  /** Growth is the whole point, so it degrades rather than disappearing. */
  private calm = false
  /** Current speed multiplier; every phase duration divides by it. */
  private tempo = 1
  /**
   * Effective birth per node for this frame.
   *
   * During replay a node's real `birth` is whenever the model produced it,
   * possibly hours ago — which would make it appear fully grown the instant it
   * is revealed. Scrubbing rewrites birth relative to the playhead so the
   * replay actually replays the growth instead of flicking nodes into being.
   */
  private birthAt = new Map<string, number>()

  /**
   * Whether anything the last frame drew was mid-transition — a strand growing,
   * a node arriving or withering, a pulse, a fold or a panel's split under way.
   * Every one of those is a pure function of the clock and would finish on its
   * own; this is what tells the loop they are not finished yet, so it keeps the
   * full rate until they are (`cadence.ts`). The ambient motion — the breath,
   * the travelling signals, a bridge's marching dash — never finishes, and is
   * deliberately not counted.
   */
  settling = false

  private born(n: ReasonNode): number {
    return this.birthAt.get(n.id) ?? n.birth
  }

  /** How strongly this node's pulse glows now — nothing under reduced motion
   *  (`pulseLevel`). */
  private pulseOf(n: ReasonNode, now: number): number {
    return pulseLevel(now - pulses.of(n), this.calm)
  }

  /** One node's own timed transitions, beyond its strand's growth. A pulse
   *  reduced motion does not draw holds no frame for it either. */
  private transient(n: ReasonNode, now: number): boolean {
    return (
      this.pulseOf(n, now) > 0 ||
      (!!n.foldAt && now - n.foldAt < FOLD_MS / this.tempo) ||
      (!!n.panel && now - n.panel.checkedAt < SPLIT_MS / this.tempo)
    )
  }

  constructor(private canvas: HTMLCanvasElement) {
    const ctx = canvas.getContext('2d', { alpha: false })
    if (!ctx) throw new Error('Canvas 2D unavailable')
    this.ctx = ctx

    const mq = window.matchMedia('(prefers-reduced-motion: reduce)')
    this.calm = mq.matches
    mq.addEventListener('change', (e) => {
      this.calm = e.matches
    })
  }

  /** Phase progressions for a strand of the given age, honouring reduced motion. */
  private phases(age: number) {
    const t = this.tempo
    const probeMs = this.calm ? 1 : PROBE_MS / t
    const extendMs = this.calm ? 170 : EXTEND_MS / t
    const consMs = this.calm ? 170 : CONSOLIDATE_MS / t
    return {
      probe: clamp(age / probeMs),
      extend: clamp((age - probeMs) / extendMs),
      consolidate: clamp((age - probeMs - extendMs) / consMs),
      nodeAt: probeMs + extendMs,
      /** When the strand has finished growing: probe, extend and consolidate. */
      grown: probeMs + extendMs + consMs,
    }
  }

  resize(w: number, h: number) {
    // Cap DPR: past 2× the extra samples cost real frames and buy nothing the
    // eye can resolve on a strand this thin.
    const dpr = Math.min(window.devicePixelRatio || 1, 2)
    if (this.w === w && this.h === h && this.dpr === dpr) return
    this.w = w
    this.h = h
    this.dpr = dpr
    this.canvas.width = Math.max(1, Math.round(w * dpr))
    this.canvas.height = Math.max(1, Math.round(h * dpr))
    groundAfterResize(this.canvas, this.ctx)
  }

  get size() {
    return { w: this.w, h: this.h }
  }

  /* ── geometry cache ───────────────────────────────────────────────── */

  private edgeFor(child: ReasonNode, parent: ReasonNode, rev: number): EdgeGeom {
    // While the layout is settling the endpoints move every frame, so the cache
    // key follows the motion epoch instead of the graph revision. Negative keys
    // keep the two numbering schemes from ever colliding.
    const key = motion.moving ? -motion.epoch - 1 : rev
    const hit = this.geom.get(child.id)
    if (hit && hit.rev === key) return hit
    const a = motion.at(parent)
    const b = motion.at(child)
    const pts = strandPath({ x: a.x, y: a.y }, { x: b.x, y: b.y }, hash01(child.id))
    const g: EdgeGeom = { pts, table: arcTable(pts), rev: key }
    this.geom.set(child.id, g)
    return g
  }

  /** The path from its start up to arc position `front`, ending exactly there. */
  private trim(g: EdgeGeom, front: number): Pt[] {
    if (front >= 0.999) return g.pts
    const out: Pt[] = []
    for (let i = 0; i < g.pts.length; i++) {
      if (g.table[i]! >= front) break
      out.push(g.pts[i]!)
    }
    out.push(pointAtArc(g.pts, g.table, Math.max(front, 0.004)))
    if (out.length < 2) out.unshift(g.pts[0]!)
    return out
  }

  /** The sub-path between two arc positions, with exact endpoints. */
  private slice(g: EdgeGeom, a: number, b: number): Pt[] {
    const out: Pt[] = [pointAtArc(g.pts, g.table, a)]
    for (let i = 0; i < g.pts.length; i++) {
      const t = g.table[i]!
      if (t > a && t < b) out.push(g.pts[i]!)
    }
    out.push(pointAtArc(g.pts, g.table, b))
    return out
  }

  /**
   * Filopodia — the hair-fine feelers a growth cone puts out to sample where
   * to go next. They are the single most recognisable thing about a growing
   * neurite, and the reason this reads as *growth* rather than as a line being
   * drawn: several directions are visibly considered before one is taken.
   *
   * Directions and rhythms are hashed from the strand's seed, so a given
   * strand always waves the same way and the timeline scrubs cleanly.
   */
  private filopodia(
    origin: Pt,
    dir: number,
    col: RGB,
    p: Palette,
    v: Viewport,
    now: number,
    seed: number,
    o: {
      count: number
      spread: number
      reach: number
      alpha: number
      lengthFor: (i: number, a: number) => number
    },
  ) {
    if (o.alpha <= 0.01 || this.calm) return
    const { ctx } = this
    // Sized in screen pixels, not world units. A feeler specified in world
    // units is a quarter of a pixel wide at the zoom you actually read the
    // graph at — present in the code and invisible on the canvas.
    const px = 1 / v.k
    const zoomEase = clamp(0.72 + v.k * 0.4, 0.72, 1.3)
    ctx.save()
    ctx.globalCompositeOperation = p.glowMode
    ctx.lineCap = 'round'

    for (let i = 0; i < o.count; i++) {
      const a = hash01(`${seed}·f${i}`)
      const fan = o.count === 1 ? 0 : (i / (o.count - 1) - 0.5) * 2 * o.spread
      // A slow independent wander per feeler — they never move in lockstep.
      const wobble = Math.sin(now * 0.0035 + a * TAU) * 0.16
      const ang = dir + fan + wobble
      const len = o.reach * px * zoomEase * (0.45 + a * 0.75) * clamp(o.lengthFor(i, a))
      if (len < px * 1.5) continue

      ctx.strokeStyle = rgba(hot(col, 0.5, p), o.alpha * (0.45 + a * 0.55))
      ctx.lineWidth = px * 1.05
      ctx.beginPath()
      ctx.moveTo(origin.x, origin.y)
      // A slight curve reads as a living filament rather than a spoke.
      const bend = (a - 0.5) * 0.5
      ctx.quadraticCurveTo(
        origin.x + Math.cos(ang - bend) * len * 0.6,
        origin.y + Math.sin(ang - bend) * len * 0.6,
        origin.x + Math.cos(ang) * len,
        origin.y + Math.sin(ang) * len,
      )
      ctx.stroke()
    }

    ctx.globalCompositeOperation = 'source-over'
    ctx.restore()
  }

  /* ── background ───────────────────────────────────────────────────── */

  private background(p: Palette, v: Viewport, rings: boolean) {
    const { ctx, w, h } = this
    const cx = v.x
    const cy = v.y

    const core = `rgb(${p.canvasCore[0]},${p.canvasCore[1]},${p.canvasCore[2]})`
    const edge = `rgb(${p.canvasEdge[0]},${p.canvasEdge[1]},${p.canvasEdge[2]})`

    // The light falls off from the seed idea. Literally: the gradient is
    // centred on the root node's screen position, so panning moves the light.
    const r = Math.max(w, h) * 0.95
    const g = ctx.createRadialGradient(cx, cy, 0, cx, cy, r)
    g.addColorStop(0, core)
    g.addColorStop(1, edge)
    ctx.fillStyle = edge
    ctx.fillRect(0, 0, w, h)
    ctx.fillStyle = g
    ctx.fillRect(0, 0, w, h)

    this.grid(p, v)
    if (rings) this.depthRings(p, v)
  }

  private grid(p: Palette, v: Viewport) {
    const { ctx, w, h } = this
    // Pick the step that keeps on-screen spacing in a comfortable band, so the
    // grid never becomes moiré at low zoom or sparse at high.
    const steps = [60, 120, 240, 480, 960, 1920]
    let step = steps[steps.length - 1]!
    for (const s of steps) {
      if (s * v.k >= 44) {
        step = s
        break
      }
    }
    const sx = step * v.k
    const startX = ((v.x % sx) + sx) % sx
    const startY = ((v.y % sx) + sx) % sx

    // A dot lattice, not ruled lines.
    //
    // There were two coincident geometries here, centred on the same point and
    // differing only by an alpha multiplier — a square grid and the depth
    // rings — which together read as one noisy texture. Only the rings carry
    // data. But the lattice is not decoration either: it is the only parallax
    // cue that tells you the canvas is moving when you pan across an empty
    // region, and the rings are culled by radius. Dots carry that same
    // reference with a fraction of the ink and, having no direction of their
    // own, nothing for the rings to compete with.
    const d = p.theme === 'dark' ? 1.0 : 0.9
    // Level of detail: it recedes as you pull back to take in the whole graph,
    // but never all the way to nothing.
    const lod = 0.45 + 0.55 * clamp((v.k - 0.35) / 0.45)
    ctx.save()
    ctx.fillStyle = rgba(p.grid, p.gridAlpha * lod)
    ctx.beginPath()
    for (let x = startX; x < w; x += sx) {
      for (let y = startY; y < h; y += sx) {
        const px = Math.round(x) + 0.5
        const py = Math.round(y) + 0.5
        // The moveTo is required: without it each arc connects to the last.
        ctx.moveTo(px + d, py)
        ctx.arc(px, py, d, 0, TAU)
      }
    }
    ctx.fill()
    ctx.restore()
  }

  /** Faint rings at each reasoning depth — the only ambient mark that is data. */
  private depthRings(p: Palette, v: Viewport) {
    const { ctx } = this
    ctx.save()
    ctx.strokeStyle = rgba(p.grid, p.ringAlpha)
    ctx.lineWidth = 1
    for (let d = 1; d <= 7; d++) {
      const r = ringRadius(d) * v.k
      if (r < 30 || r > Math.max(this.w, this.h) * 1.6) continue
      ctx.beginPath()
      ctx.arc(v.x, v.y, r, 0, TAU)
      ctx.stroke()
    }
    ctx.restore()
  }

  /* ── strands ──────────────────────────────────────────────────────── */

  private strandColour(n: ReasonNode, p: Palette, ch = channelOf(n)): RGB {
    const base = p.channel[ch]
    // Tension bends any strand toward the contradiction hue in proportion to
    // how hard it fights its parent — so friction is visible before you click.
    //
    // `refuted` is excluded: bending it toward tension is exactly the confusion
    // the hue exists to prevent. A contradiction is the graph arguing with itself;
    // a refutation is the world disagreeing with the graph, and a magenta strand
    // three-quarters of the way to coral says neither clearly.
    //
    // Read through `drawnRatingsOf`: a rewritten claim's tension rated the words
    // the reader replaced, and bending her sentence toward the contradiction hue
    // by it is the canvas printing a number the card no longer does.
    const { tension } = drawnRatingsOf(n)
    if (ch !== 'tension' && ch !== 'dead' && ch !== 'refuted' && tension > 0.28) {
      return mix(base, p.channel.tension, clamp((tension - 0.28) / 0.72) * 0.55)
    }
    return base
  }

  private drawStrand(
    child: ReasonNode,
    parent: ReasonNode,
    p: Palette,
    v: Viewport,
    now: number,
    rev: number,
    dim: number,
    hotv: number,
  ) {
    const { ctx } = this
    const g = this.edgeFor(child, parent, rev)

    const age = now - this.born(child)
    if (age <= 0) return
    const { probe, extend, consolidate } = this.phases(age)
    // The inference's weight, as drawn — the middle of the scale where the
    // reader rewrote the claim and nothing rated her words.
    const confidence = drawnRatingsOf(child).confidence

    // A node carrying a recorded result is never dead, whatever the model thought.
    // `terminal` is the model's own judgement that a path is exhausted, and it must
    // not be able to grey out the only measured thing in the exploration.
    const dead = child.terminal && !child.outcome
    const seed = hash01(child.id + '·g')

    // A dead end is explored before it is abandoned. The strand grows in the
    // colour of whatever it actually was — a contradiction, a gap — and only
    // then drains to grey and thins away. Arriving pre-dead would hide the
    // most informative half of the story.
    const nodeAtMs = (PROBE_MS + EXTEND_MS) / this.tempo
    const wither = !dead
      ? 0
      : this.calm
        ? 1
        : clamp((age - nodeAtMs - 300 / this.tempo) / (900 / this.tempo))
    const liveCh = liveChannelOf(child)
    const live = this.strandColour(child, p, liveCh)
    // The geometry still withers — a dead end still thins and shrinks — but the
    // COLOUR drain is skipped for a refutation. Draining magenta to grey would take
    // the strongest statement on the canvas and hide it behind the model's opinion
    // that the path is finished.
    const col = dead && liveCh !== 'refuted' ? mix(live, p.channel.dead, wither) : live

    // Divergence, drawn as form. One strand rendered as two that never rejoin: the
    // panel read this node three ways and the reading did not close. It stays under
    // reduced motion, because it is data rather than motion — only the parting is
    // an animation, and that is a pure function of `checkedAt`.
    const split = panelSplit(child.panel) && v.k >= SPLIT_MIN_K
    const splitOpen = !split
      ? 0
      : this.calm
        ? 1
        : easeOutCubic(clamp((now - (child.panel?.checkedAt ?? 0)) / (SPLIT_MS / this.tempo)))
    // A subtree reasoned before the result above it came in. Detached at the
    // parent, because that is the join the result invalidated — and at every
    // join below it too, where what came in was a refutation.
    const stale = this.staleParent(child) || this.isTainted(child) || this.isEvidenceStale(child)

    ctx.save()
    ctx.translate(v.x, v.y)
    ctx.scale(v.k, v.k)

    // ── PROBE ────────────────────────────────────────────────────────────
    // Before anything commits, the parent puts out feelers. Several
    // directions get sampled; all but one pull back.
    if (probe < 1) {
      const from = motion.at(parent)
      const to = motion.at(child)
      const dir = Math.atan2(to.y - from.y, to.x - from.x)
      this.filopodia(from, dir, col, p, v, now, seed, {
        count: 5,
        spread: 1.15,
        reach: 30,
        alpha: 0.5 * dim * Math.sin(probe * Math.PI) ** 0.6,
        // The winner keeps extending; the others reach out and retract.
        lengthFor: (i, a) =>
          i === Math.floor(seed * 5) ? probe : Math.sin(clamp(probe * (0.55 + a * 0.8)) * Math.PI),
      })
      ctx.restore()
      return
    }

    // ── EXTEND ───────────────────────────────────────────────────────────
    const front = extend >= 1 ? 1 : saltatory(extend, seed)
    // A stale strand starts short of its parent. Built from `slice` rather than by
    // trimming the front, so the gap is at the premise end where it belongs.
    const pts = stale && front > DETACH ? this.slice(g, DETACH, front) : this.trim(g, front)

    // Width: full at the premise, tapering to the claim — but only *behind*
    // the consolidation wave. Ahead of it the shaft is a thin, immature
    // neurite that has reached but not yet committed.
    const wFull = (1.15 + confidence * 3.5) * p.strandScale
    // A withered strand still thins toward the claim — but 0.04 of full width
    // is sub-pixel at any zoom you actually read the graph at, so the last
    // third of a dead strand was not being drawn at all. 0.11 is still a
    // visibly spent line; it is just a line.
    const wTip = wFull * (dead ? 0.3 + (0.11 - 0.3) * wither : 0.3)
    const wImmature = wFull * 0.26
    const cf = consolidate
    // A detached strand's own arc runs DETACH..1, so its taper parameter has to be
    // remapped into that span — otherwise the taper is rescaled and a stale strand
    // reads thinner along its whole length rather than simply starting later.
    const span = (t: number) => (stale ? DETACH + t * (1 - DETACH) : t)
    const half = (t: number) => {
      const u = span(t)
      const mature = (wFull + (wTip - wFull) * u) / 2
      const young = (wImmature / 2) * (1 - u * 0.3)
      const m = smoothstep(cf, cf + 0.18, u) // 0 behind the wave, 1 ahead of it
      const fade = dead ? 1 - u * 0.76 * wither : 1
      return Math.max(0.18 / v.k, (mature * (1 - m) + young * m) * fade)
    }

    const maturity = 0.42 + 0.58 * cf
    const liveAlpha = 0.72 + confidence * 0.28
    const alphaBase =
      (dead ? liveAlpha + (0.44 - liveAlpha) * wither : liveAlpha) * p.baseAlpha * dim * maturity

    // Whether this strand's polygons are a pure function of what the path cache
    // is keyed on. Growth (`extend`, `cf`), a withering in progress and a
    // parting in progress all change per frame and are tessellated the old way;
    // everything else — which is every strand on a graph at rest — is rebuilt
    // only when an input moves. Re-tessellating every strand every frame was
    // a quarter of the canvas's own time and most of its garbage
    // (docs/performance-audit.md, finding 2).
    const settled =
      extend >= 1 &&
      cf >= 1 &&
      (wither === 0 || wither === 1) &&
      (splitOpen === 0 || splitOpen === 1)
    const kb = zoomBucket(v.k)

    // Halo. Additive on dark; a soft wide underlay on light, where adding
    // light to paper would only wash the strand out.
    if (wither < 0.9) {
      const spread = 1 + (hotv * 1.5 + confidence * 0.8)
      ctx.globalCompositeOperation = p.glowMode
      // A neutral cast shade on paper, an emitted glow on the night field.
      ctx.fillStyle = rgba(
        washed(col, p),
        (p.theme === 'dark' ? 0.1 : 0.05) * (0.5 + hotv) * dim * maturity * (1 - wither),
      )
      if (settled) {
        // Quantised, so a fading pulse rebuilds its halo every few frames
        // rather than on every one; at 1/50 the width difference is invisible.
        const spreadQ = Math.round(spread * 50) / 50
        ctx.fill(
          this.strandPath(g, 'halo', kb, stale, wFull, wTip, splitOpen, spreadQ, () => [
            ribbon(pts, (t) => half(t) * spreadQ * 2.2),
          ]),
        )
      } else {
        tracePoly(
          ctx,
          ribbon(pts, (t) => half(t) * spread * 2.2),
        )
        ctx.fill()
      }
      ctx.globalCompositeOperation = 'source-over'
    }

    // Core.
    ctx.fillStyle = rgba(hot(col, hotv * 0.26, p), alphaBase)
    const corePolys = (): Pt[][] => {
      if (splitOpen > 0) {
        // Two ribbons, parting from the parent and never rejoining. Each carries
        // slightly over half the width so the pair reads as one strand that came
        // apart rather than as two strands that were always separate — and the
        // minimum-width floor is applied AFTER the halving, or a contested strand
        // ends up fainter than an ordinary one at low zoom, which is backwards.
        // Deliberately NOT scaled by `wither`. A panel that disagreed about a path
        // the model then declared exhausted is the most useful version of this state,
        // and scaling the parting away on a withered strand hid exactly that case.
        // The strand still withers in width and colour, so it does not compete — it
        // simply also runs as two.
        const reach = (SPLIT_PX / v.k) * splitOpen
        // Zero at the premise, full at the claim: the disagreement is about where
        // this node lands, not about where it came from.
        const spreadAt = (t: number) => reach * span(t)
        const halfOf = (k: number) => (t: number) => Math.max(0.18 / v.k, half(t) * k)
        return [-1, 1].map((sgn) =>
          ribbon(
            offsetPath(pts, (t) => spreadAt(t) * sgn),
            halfOf(sgn < 0 ? 0.58 : 0.5),
          ),
        )
      }
      return [ribbon(pts, half)]
    }
    if (settled) {
      ctx.fill(this.strandPath(g, 'core', kb, stale, wFull, wTip, splitOpen, 0, corePolys))
    } else {
      for (const poly of corePolys()) {
        tracePoly(ctx, poly)
        ctx.fill()
      }
    }

    // ── CONSOLIDATE ──────────────────────────────────────────────────────
    // A bright band riding the boundary between mature and immature shaft.
    // This is the beat where a tentative link becomes a real one.
    //
    // Suppressed on a split strand: the band means "this link just became real",
    // and a link three lenses could not agree on has not.
    if (cf > 0 && cf < 1 && wither < 0.5 && splitOpen === 0) {
      const a = Math.sin(cf * Math.PI) ** 0.7
      const seg = this.slice(g, Math.max(0, cf - 0.07), Math.min(1, cf + 0.02))
      if (seg.length >= 2) {
        ctx.globalCompositeOperation = p.glowMode
        ctx.fillStyle = rgba(hot(col, 0.55, p), 0.4 * a * dim)
        tracePoly(
          ctx,
          ribbon(seg, (t) => half(cf) * (0.55 + t * 0.7)),
        )
        ctx.fill()
        ctx.globalCompositeOperation = 'source-over'
      }
    }

    // ── GROWTH CONE ──────────────────────────────────────────────────────
    // The bulbous, filopodia-crowned tip. It only exists while the strand is
    // still finding its target, which is exactly when you want to look at it.
    if (extend < 1) {
      const tip = pointAtArc(g.pts, g.table, front)
      const back = pointAtArc(g.pts, g.table, Math.max(0, front - 0.05))
      const dir = Math.atan2(tip.y - back.y, tip.x - back.x)
      const settle = 1 - smoothstep(0.86, 1, extend) // flattens on approach

      this.filopodia({ x: tip.x, y: tip.y }, dir, col, p, v, now, seed, {
        count: 4,
        spread: 0.78,
        reach: 26,
        alpha: 0.55 * dim * settle,
        lengthFor: (_i, a) => 0.45 + 0.55 * Math.sin(now * 0.009 + a * TAU),
      })

      const px = 1 / v.k
      const zoomEase = clamp(0.72 + v.k * 0.4, 0.72, 1.3)
      const r = (2.7 + confidence * 1.5) * px * zoomEase * (0.65 + settle * 0.35)
      ctx.globalCompositeOperation = p.glowMode
      ctx.fillStyle = rgba(col, 0.3 * dim)
      ctx.beginPath()
      ctx.arc(tip.x, tip.y, r * 3.4, 0, TAU)
      ctx.fill()
      ctx.fillStyle = rgba(hot(col, 0.8, p), 0.95 * dim)
      ctx.beginPath()
      ctx.arc(tip.x, tip.y, r, 0, TAU)
      ctx.fill()
      ctx.globalCompositeOperation = 'source-over'
    }

    // Travelling signal. Position is a pure function of time and a per-strand
    // seed — no particle state to keep, and it stays deterministic under pause.
    //
    // Drawn as two stacked discs rather than a radial gradient: a gradient per
    // particle per frame means hundreds of `createRadialGradient` allocations a
    // second on a large graph, and at this size the difference is invisible.
    // Suppressed on a detached strand: a travelling signal means current is
    // flowing along a live path, and a path whose premise has been overtaken by a
    // result is exactly the path where it is not.
    if (this.showSignal(child, p, hotv) && !dead && !stale && consolidate >= 1 && this.budget > 0) {
      const activity = 0.16 + hotv * 0.84
      const speed = 0.00016 + confidence * 0.00022
      const seed = hash01(child.id + '·p')
      const n = hotv > 0.4 ? Math.min(3, this.budget) : 1
      this.budget -= n
      const r =
        (1.5 + confidence * 1.4) * (0.7 + hotv) * (1 / v.k) * clamp(0.72 + v.k * 0.4, 0.72, 1.3)
      ctx.globalCompositeOperation = p.glowMode
      for (let i = 0; i < n; i++) {
        const s = (now * speed + seed + i / n) % 1
        const pt = pointAtArc(g.pts, g.table, s)
        ctx.fillStyle = rgba(col, 0.2 * activity * dim)
        ctx.beginPath()
        ctx.arc(pt.x, pt.y, r * 3, 0, TAU)
        ctx.fill()
        ctx.fillStyle = rgba(hot(col, 0.6, p), 0.85 * activity * dim)
        ctx.beginPath()
        ctx.arc(pt.x, pt.y, r, 0, TAU)
        ctx.fill()
      }
      ctx.globalCompositeOperation = 'source-over'
    }

    // A recorded result: one solid bar across the strand, just short of the node.
    //
    // On the strand rather than on the disc, because what the result attaches to is
    // the derivation — this is the point where the reasoning stopped being only
    // reasoning. In the accent, which is the only colour in this app that means
    // "you did this"; every other hue on the canvas belongs to the model.
    //
    // Near the CLAIM end rather than the premise end, which is the second attempt.
    // At the premise end the bar is off screen the moment the camera centres on the
    // node it describes — a mark you cannot find from the thing it marks is not a
    // mark. 0.72 clears the disc, its rings, and the fold chevrons at 4r.
    if (child.outcome && consolidate > 0.2) {
      const at = pointAtArc(g.pts, g.table, 0.72)
      const back = pointAtArc(g.pts, g.table, 0.66)
      const dx = at.x - back.x
      const dy = at.y - back.y
      const m = Math.hypot(dx, dy) || 1
      const arm = Math.max(2.6, (wFull / 2) * 2.6)
      ctx.strokeStyle = rgba(p.accent, 0.92 * dim * p.baseAlpha)
      ctx.lineWidth = Math.max(1, wFull * 0.5)
      ctx.lineCap = 'round'
      ctx.beginPath()
      ctx.moveTo(at.x - (-dy / m) * arm, at.y - (dx / m) * arm)
      ctx.lineTo(at.x + (-dy / m) * arm, at.y + (dx / m) * arm)
      ctx.stroke()
      ctx.lineCap = 'butt'
    }

    ctx.restore()
  }

  /**
   * A settled strand's polygon(s) as a `Path2D`, rebuilt only when an input
   * changes.
   *
   * The centre line was already cached per strand and per revision (`edgeFor`);
   * the two ribbons built on it — the halo and the core — were not, so every
   * frame tessellated both again, allocated three arrays of ~54 points for each
   * and replayed ~54 `lineTo`s into the context, then dropped the lot. On a
   * graph at rest that was a quarter of the canvas's time and most of the
   * garbage collector's. A path keyed on everything the polygon depends on is
   * built once and filled thereafter; a change to any input — a pulse fading,
   * a zoom crossing a bucket, a theme's strand scale — rebuilds that strand
   * alone. The `EdgeGeom` it hangs off is replaced whenever the centre line is,
   * so a moving layout drops these with it and nothing is ever stale.
   */
  private strandPath(
    g: EdgeGeom,
    slot: 'core' | 'halo',
    kb: number,
    stale: boolean,
    wFull: number,
    wTip: number,
    split: number,
    spread: number,
    build: () => Pt[][],
  ): Path2D {
    const hit = g[slot]
    if (
      hit &&
      hit.rev === g.rev &&
      hit.kb === kb &&
      hit.stale === stale &&
      hit.wFull === wFull &&
      hit.wTip === wTip &&
      hit.split === split &&
      hit.spread === spread
    ) {
      return hit.path
    }
    const path = new Path2D()
    for (const poly of build()) addPoly(path, poly)
    g[slot] = { path, rev: g.rev, kb, stale, wFull, wTip, split, spread }
    return path
  }

  /**
   * A node halo's gradient, cached.
   *
   * A radial gradient was created per haloed node per frame — a few hundred
   * allocations and stop parses a frame on a large graph, for gradients that
   * are identical from one frame to the next for every node at rest. Built
   * once at the origin, keyed on its spread, hue and alpha (both quantised
   * below what the eye separates), and drawn under a translate, so the same
   * gradient serves every node that wants it. That works because a canvas
   * gradient's coordinates are read in the context's user space at PAINT time,
   * not captured when it is created — `translate(x, y)` then a fill at the
   * origin paints the gradient centred on the node. (Checked in Chromium: a
   * gradient built at (0, 0) and filled after `translate(200, 150)` is opaque
   * at (200, 150) and empty at the origin.) The map is cleared past a ceiling
   * rather than evicted, because a zoom sweeps through every spread once and
   * then settles; a settled graph refills it in one frame.
   */
  private haloGradient(hue: RGB, a: number, spread: number): { g: CanvasGradient; r: number } {
    const r = Math.round(spread * 2) / 2
    const aq = Math.round(a * 400) / 400
    const key = `${r}|${hue[0]},${hue[1]},${hue[2]}|${aq}`
    let g = this.gradients.get(key)
    if (!g) {
      if (this.gradients.size > 1024) this.gradients.clear()
      g = this.ctx.createRadialGradient(0, 0, 0, 0, 0, r)
      // A glow, not a wash. Light falls off with the square of distance, and a
      // ramp interpolated straight from core to rim puts half the halo's energy
      // outside its own mid-line — into exactly the annuli where neighbouring
      // haloes meet. These stops approximate the square-law decay: most of the
      // energy inside the first quarter of the spread, almost none at the rim.
      g.addColorStop(0, rgba(hue, aq))
      g.addColorStop(0.25, rgba(hue, aq * 0.42))
      g.addColorStop(0.55, rgba(hue, aq * 0.12))
      g.addColorStop(1, rgba(hue, 0))
      this.gradients.set(key, g)
    }
    return { g, r }
  }

  /**
   * Whether this strand was reasoned before a result above it came in.
   *
   * Compared against `recordedAt`, not `at`: `at` is when the observation happened
   * in the world, which can predate the whole exploration. What matters for a piece
   * of reasoning is whether whoever wrote it knew the result yet.
   *
   * Walks only to the nearest ancestor that has one, so a deep subtree under a
   * measured node does not pay for the whole chain on every frame.
   */
  private staleParent(child: ReasonNode): boolean {
    if (!child.parentId) return false
    const p = this.nodesNow?.[child.parentId]
    if (!p?.outcome) return false
    // When its words were written — a rewrite's date, where the reader rewrote
    // it after the result — as the taint walk dates it (`writtenAt`).
    return writtenAt(child) < p.outcome.recordedAt
  }

  /**
   * The same mark, at any distance.
   *
   * `staleParent` above asks one hop, which is all it can do from a node table:
   * a grandchild of a measured claim has an unmeasured parent and reads as
   * attached. That was never the mark's meaning — "reasoned before the result
   * above it" is true of the whole subtree — so where the result REFUTED the
   * claim, `taint.ts` carries it the rest of the way and the strand detaches
   * wherever it lands. The narrower case (a result of any bearing, one hop) is
   * still `staleParent`'s: a supporting result does not unmoor what was reasoned
   * under it, so the two conditions are a union rather than a replacement.
   */
  private isTainted(child: ReasonNode): boolean {
    return this.taintNow?.has(child.id) ?? false
  }

  /** Whether a changed source record postdates this dependency path. */
  private isEvidenceStale(child: ReasonNode): boolean {
    return this.evidenceStaleNow?.has(child.id) ?? false
  }

  /** Set once per frame in `draw`, so the strand pass can reach a parent without
   *  threading the session through every call. */
  private nodesNow: Record<string, ReasonNode> | null = null
  /** Likewise, for the refutation each node stands on. See `isTainted`. */
  private taintNow: ReadonlyMap<string, Taint> | null = null
  /** Likewise, for every dated source change a dependency has not incorporated. */
  private evidenceStaleNow: ReadonlyMap<string, EvidenceStale> | null = null
  /** Likewise, for every claim an objection below it stood against (`canvas/notch.ts`). */
  private stoodNow: ReadonlySet<string> | null = null

  /**
   * Which strands carry a visible signal. Light theme needs a higher bar — a
   * travelling highlight on paper reads as noise rather than as flow — and on
   * a large graph only the live and the well-supported paths earn one, so the
   * effect stays a signal rather than becoming ambient sparkle.
   */
  private showSignal(n: ReasonNode, p: Palette, hotv: number): boolean {
    if (!this.signals) return false
    if (hotv > 0.05) return true
    return drawnRatingsOf(n).confidence > (p.theme === 'dark' ? 0.62 : 0.72)
  }
  private signals = true
  /** Per-frame particle budget, so a 400-node graph cannot melt the frame. */
  private budget = 0

  /** A synthesis node answers to two parents; the second link arcs across. */
  private drawBridge(
    child: ReasonNode,
    other: ReasonNode,
    p: Palette,
    v: Viewport,
    now: number,
    dim: number,
  ) {
    const { ctx } = this
    const grow = clamp((now - this.born(child)) / (this.phases(0).grown * 0.9))
    if (grow <= 0.001) return
    const oa = motion.at(other)
    const ob = motion.at(child)
    const pts = strandPath({ x: oa.x, y: oa.y }, { x: ob.x, y: ob.y }, hash01(child.id + '·b'), 34)
    const count = Math.max(2, Math.ceil(easeOutCubic(grow) * (pts.length - 1)) + 1)
    const stale = this.isTainted(child) || this.isEvidenceStale(child)
    const start = stale ? Math.ceil((pts.length - 1) * DETACH) : 0
    if (count <= start + 1) return
    const sl = pts.slice(start, count)
    const col = p.channel.insight

    ctx.save()
    ctx.translate(v.x, v.y)
    ctx.scale(v.k, v.k)
    ctx.globalCompositeOperation = p.glowMode
    ctx.fillStyle = rgba(washed(col, p), (p.theme === 'dark' ? 0.075 : 0.05) * dim)
    tracePoly(
      ctx,
      ribbon(sl, () => 3.6),
    )
    ctx.fill()
    ctx.globalCompositeOperation = 'source-over'

    // Dashed, because it is an asserted connection rather than a derived one.
    ctx.strokeStyle = rgba(col, 0.6 * dim * p.baseAlpha)
    ctx.lineWidth = Math.max(0.5 / v.k, 1.1)
    ctx.setLineDash([5, 6])
    ctx.lineDashOffset = -now * 0.012
    ctx.beginPath()
    ctx.moveTo(sl[0]!.x, sl[0]!.y)
    for (let i = 1; i < sl.length; i++) ctx.lineTo(sl[i]!.x, sl[i]!.y)
    ctx.stroke()
    ctx.setLineDash([])
    ctx.restore()
  }

  /**
   * The dispatch pulse.
   *
   * While the model is working on a branch, a bright signal runs from the seed
   * outward along the lineage to the node being worked on, then repeats. It
   * answers the question a growing graph otherwise leaves open — *where* is it
   * thinking right now — using the graph itself rather than a caption.
   */
  private dispatch(f: FrameInput, now: number) {
    if (this.calm || !f.busy || !f.activeTargets.length) return
    const target = f.activeTargets[0]!
    const { session, view: v, palette: p } = f

    // Root → target, as a list of the strands between them.
    const chain: ReasonNode[] = []
    const guard = new Set<string>()
    let cur: string | null = target
    while (cur && !guard.has(cur)) {
      guard.add(cur)
      const n: ReasonNode | undefined = session.nodes[cur]
      if (!n) break
      if (n.parentId !== null && f.visible.has(n.id)) chain.unshift(n)
      cur = n.parentId
    }
    if (!chain.length) return

    const { ctx } = this
    const px = 1 / v.k
    ctx.save()
    ctx.translate(v.x, v.y)
    ctx.scale(v.k, v.k)
    ctx.globalCompositeOperation = p.glowMode

    // One head travelling the whole chain, plus a second half a cycle behind
    // so a long lineage never looks empty between pulses.
    for (const offset of [0, 0.5]) {
      const q = ((now / DISPATCH_MS + offset) % 1) * chain.length
      const idx = Math.floor(q)
      const node = chain[idx]
      if (!node) continue
      const g = this.geom.get(node.id)
      if (!g) continue

      const local = q - idx
      const col = this.strandColour(node, p)
      // A short comet rather than a dot: three samples fading behind the head.
      for (let t = 0; t < 3; t++) {
        const at = local - t * 0.055
        if (at < 0) continue
        const pt = pointAtArc(g.pts, g.table, at)
        const a = (1 - t / 3) * 0.9
        ctx.fillStyle = rgba(col, 0.22 * a)
        ctx.beginPath()
        ctx.arc(pt.x, pt.y, (5.4 - t * 1.1) * px, 0, TAU)
        ctx.fill()
        ctx.fillStyle = rgba(hot(col, 0.85, p), 0.95 * a)
        ctx.beginPath()
        ctx.arc(pt.x, pt.y, (2.1 - t * 0.45) * px, 0, TAU)
        ctx.fill()
      }
    }

    ctx.globalCompositeOperation = 'source-over'
    ctx.restore()
  }

  /* ── nodes ────────────────────────────────────────────────────────── */

  private drawRoot(
    n: ReasonNode,
    kids: ReasonNode[],
    p: Palette,
    v: Viewport,
    now: number,
    busy: boolean,
    selected: boolean,
    hovered: boolean,
  ) {
    const { ctx } = this
    const { x, y, rx, ry } = this.seedClear(n, v)
    const acc = p.channel.signal

    ctx.save()

    // Bare: no aura and no body. The seed is its title (`SeedLabel.tsx`), and a
    // glow under the title was a third thing competing with the words and the
    // strands for the middle of the canvas. What is left is temporal or
    // interactive, drawn on the clear field's edge. The contour appears only
    // while the seed is hovered or held, because the seed has no disc for the
    // selection ring and its caption never takes the selection rule.
    const edge = selected ? 1 : hovered ? 0.55 : 0
    if (edge > 0) {
      ctx.strokeStyle = rgba(
        p.theme === 'dark' ? (selected ? lift(acc, 0.45) : acc) : selected ? p.deepen : p.wash,
        0.18 + edge * 0.34,
      )
      ctx.lineWidth = (p.theme === 'dark' ? 1 : 1.1) + edge * 0.4
      ctx.beginPath()
      ctx.ellipse(x, y, rx, ry, 0, 0, TAU)
      ctx.stroke()
    }

    this.drawBranchArcs(n, kids, p, v, now, x, y, rx)

    // While working, a ring sweeps outward — the only purely temporal mark on
    // the canvas, and it is load-bearing: it says the model is still going.
    if (busy) {
      const t = (now % DISPATCH_MS) / DISPATCH_MS
      const g = 1 + t * 0.35
      ctx.strokeStyle = rgba(acc, 0.3 * (1 - t))
      ctx.lineWidth = 1.4
      ctx.beginPath()
      ctx.ellipse(x, y, rx * g, ry * g, 0, 0, TAU)
      ctx.stroke()
    }
    ctx.restore()
  }

  /**
   * The rim of the seed's clear field, drawn as one arc per first-ring branch,
   * each in the colour of the strand that leaves it.
   *
   * Each arc is centred where its strand crosses the rim — read off the
   * strand's own path, which bends, rather than the node's angle — and runs
   * halfway to its neighbours' crossings, less a gap, so the rim reads as the
   * branches' shares of the seed. The colour is the strand's (`strandColour`,
   * the dead-end grey for a withered one), so an arc and the strand it feeds
   * are one mark. An arc fades in with its branch's growth. No ticks and no
   * scanner: the rim is a partition, not an instrument (D350).
   */
  private drawBranchArcs(
    root: ReasonNode,
    kids: ReasonNode[],
    p: Palette,
    v: Viewport,
    now: number,
    x: number,
    y: number,
    r: number,
  ) {
    const { ctx } = this
    if (r < 6) return
    const at = motion.at(root)
    const rw = r / v.k
    const marks: { a: number; col: RGB; alpha: number }[] = []
    for (const c of kids) {
      const g = this.edgeFor(c, root, this.lastRev)
      let pt = g.pts[g.pts.length - 1]!
      for (const q of g.pts) {
        if (Math.hypot(q.x - at.x, q.y - at.y) >= rw) {
          pt = q
          break
        }
      }
      const ch = liveChannelOf(c)
      const dead = c.terminal && !c.outcome && ch !== 'refuted'
      const col = dead ? p.channel.dead : this.strandColour(c, p, ch)
      const grow = this.calm ? 1 : clamp((now - this.born(c)) / (700 / this.tempo))
      marks.push({ a: Math.atan2(pt.y - at.y, pt.x - at.x), col, alpha: grow })
    }
    marks.sort((m1, m2) => m1.a - m2.a)

    ctx.save()
    ctx.lineCap = 'round'
    ctx.lineWidth = p.theme === 'dark' ? 2 : 2.2
    const gap = Math.min(0.12, 9 / r)
    const n = marks.length
    for (let i = 0; i < n; i++) {
      const m = marks[i]!
      let from: number
      let to: number
      if (n === 1) {
        from = m.a - Math.PI + gap
        to = m.a + Math.PI - gap
      } else {
        const prev = marks[(i - 1 + n) % n]!.a - (i === 0 ? TAU : 0)
        const next = marks[(i + 1) % n]!.a + (i === n - 1 ? TAU : 0)
        from = (prev + m.a) / 2 + gap / 2
        to = (m.a + next) / 2 - gap / 2
      }
      if (to <= from || m.alpha <= 0) continue
      ctx.strokeStyle = rgba(m.col, (p.theme === 'dark' ? 0.85 : 0.75) * m.alpha)
      ctx.beginPath()
      ctx.arc(x, y, r, from, to)
      ctx.stroke()
    }

    ctx.restore()
  }

  /**
   * The seed's clear field, screen px: a circle round the caption's on-screen
   * box plus air, so there is the same room on every side of the title, never
   * smaller than `ROOT_CLEAR` and never reaching the first ring's nodes.
   */
  private seedClear(n: ReasonNode, v: Viewport) {
    const at = motion.at(n)
    const floor = ROOT_CLEAR * v.k
    // Well inside the first ring, so the outer part of every first-ring strand
    // is always drawn: zoomed far out the caption is at its smallest scale but
    // can still be wide against the ring, and a field fitted to it would
    // swallow the branches it should sprout.
    const cap = Math.max(floor, seedRingCap(v.k))
    // Round the caption's ink plus its air, and never past `SEED_RIM_MAX` of
    // the way to the first ring's nodes: the caption shrinks to fit inside
    // that first (`seedScale`), so the words stand clear of the line at every
    // zoom a reader reaches, and the outline never swallows the nodes it
    // feeds. Only past the caption's floor, zoomed out beyond that, could the
    // two meet — and then the nodes win.
    const need = seedRimFor(screen.seedW, screen.seedH, screen.seedScale)
    const hard = ringRadius(1) * SEED_RIM_MAX * v.k
    const r = Math.min(hard, Math.max(need, Math.min(cap, Math.max(floor, need))))
    return { x: v.x + at.x * v.k, y: v.y + at.y * v.k, rx: r, ry: r }
  }

  private drawNode(
    n: ReasonNode,
    p: Palette,
    v: Viewport,
    now: number,
    dim: number,
    selected: boolean,
    hovered: boolean,
    folded: number,
  ) {
    const { ctx } = this
    const age = now - this.born(n)
    const { nodeAt } = this.phases(age)
    // The node lands the moment the growth cone touches down, and springs
    // slightly past its size before settling — the one overshoot on the canvas,
    // spent on the single event worth celebrating.
    const grow = clamp((age - nodeAt) / (this.calm ? 160 : 560 / this.tempo))
    if (grow <= 0.001) return
    const e = this.calm ? grow : easeOutBack(grow, 2.2)

    const at = motion.at(n)
    const x = v.x + at.x * v.k
    const y = v.y + at.y * v.k

    // Same wither clock as the strand: a terminal node lands showing what it
    // was, then drains and shrinks into a spent marker.
    const dead = n.terminal && !n.outcome
    const wither = !dead
      ? 0
      : this.calm
        ? 1
        : clamp((age - nodeAt - 300 / this.tempo) / (900 / this.tempo))
    const liveCh = liveChannelOf(n)
    const live = this.strandColour(n, p, liveCh)
    const refuted = liveCh === 'refuted'
    const col = dead && !refuted ? mix(live, p.channel.dead, wither) : live
    // GLYPHS come from the kind, COLOUR from the channel. A refuted gap is a
    // hollow ring in magenta; a refuted contradiction keeps its cross. Reading the
    // glyph off `liveChannelOf` instead would make a refutation erase the fact that
    // the node was a contradiction, which is information the reader needs to judge
    // what the refutation actually refuted.
    const kindCh = kindChannelOf(n)
    const insight = kindCh === 'insight' && !dead
    // An open question, or a conjecture nothing outside the model has tested:
    // both have no centre yet. One predicate for both cameras — `hollowGlyph`.
    const hollow = hollowGlyph(n)
    const tension = kindCh === 'tension'

    // Size is what the node earns: novel and well-supported reads largest.
    // Only *partly* scaled by zoom — a node that vanishes when you pull back to
    // see the whole graph is a node you cannot use to see the whole graph.
    const zoomFactor = clamp(0.62 + v.k * 0.42, 0.62, 1.45)
    // A claim the reader rewrote sits at the middle of the scale: nothing rated
    // her words, and the card prints no number for them (`drawnRatingsOf`).
    const rated = drawnRatingsOf(n)
    const r =
      (2.9 + rated.novelty * 3.8 + rated.confidence * 1.6) * (1 - wither * 0.3) * e * zoomFactor

    // ── ARRIVAL ──────────────────────────────────────────────────────────
    // What the touchdown looks like depends on what arrived, because the four
    // outcomes mean different things and should not feel the same.
    const arrive = clamp((age - nodeAt) / (this.calm ? 1 : ARRIVE_MS / this.tempo))
    if (arrive > 0 && arrive < 1) {
      const fade = (1 - arrive) ** 2
      ctx.save()
      ctx.globalCompositeOperation = p.glowMode
      if (dead) {
        // A path that died: the ring collapses inward instead of blooming.
        ctx.strokeStyle = rgba(live, 0.5 * fade * dim)
        ctx.lineWidth = 1.2
        ctx.beginPath()
        ctx.arc(x, y, r * (4.5 - arrive * 3.5), 0, TAU)
        ctx.stroke()
      } else if (tension || refuted) {
        // A contradiction lands hard: two quick, tight rings. A refutation lands
        // the same way, because it is the same kind of event arriving from a
        // different authority — and it is the one arrival on this canvas that a
        // reader should not be able to miss.
        for (const off of [0, 0.22]) {
          const t = clamp((arrive - off) / 0.55)
          if (t <= 0 || t >= 1) continue
          ctx.strokeStyle = rgba(col, 0.75 * (1 - t) ** 2 * dim)
          ctx.lineWidth = 1.4
          ctx.beginPath()
          ctx.arc(x, y, r * (1 + t * 4), 0, TAU)
          ctx.stroke()
        }
      } else {
        // Everything else blooms — insight widest and slowest.
        const reach = insight ? 9 : 5
        ctx.strokeStyle = rgba(hot(col, 0.4, p), (insight ? 0.8 : 0.55) * fade * dim)
        ctx.lineWidth = insight ? 1.6 : 1.1
        ctx.beginPath()
        ctx.arc(x, y, r * (1 + easeOutCubic(arrive) * reach), 0, TAU)
        ctx.stroke()
        ctx.fillStyle = rgba(hot(col, 0.7, p), 0.5 * fade * dim)
        ctx.beginPath()
        ctx.arc(x, y, r * (1 + arrive * 1.6), 0, TAU)
        ctx.fill()
      }
      ctx.globalCompositeOperation = 'source-over'
      ctx.restore()
    }

    const pulse = this.pulseOf(n, now)
    const pulsing = pulse > 0

    ctx.save()

    // Halo — novelty made visible.
    //
    // Suppressed on a refuted node, and that is a deliberate zero-sum trade: the
    // magenta REPLACES the novelty bloom rather than being added on top of it, so
    // introducing the loudest state on the canvas does not increase the total
    // saturated area. A refuted claim's novelty is also the least interesting thing
    // about it.
    // No bloom for a claim with no rating to bloom by (`drawnGlowOf`), except the
    // ones a hand or a landing lights.
    const glow = drawnGlowOf(n)
    if (!dead && !refuted && (glow > 0.25 || pulsing || selected || hovered)) {
      // The bloom compresses with the camera where the disc must not. The disc
      // keeps a zoom floor so a node stays findable from far out — but a glow
      // holding the same floor is what turned a distant graph into fog: spacing
      // shrinks with `k`, extent does not, and past the crossover every halo
      // overlaps its neighbours and `lighter` sums the overlap into one cloud.
      // A light seen from far away is a point with a tight bloom, so extent and
      // energy both track the true zoom down.
      //
      // Note what the arithmetic actually says, because the obvious reading of
      // it is wrong: this reaches 1 only at k ≥ 1, and framing a graph puts k
      // well under that, so a fitted view is damped too — by a third or so at a
      // typical fit. That is intended rather than incidental. The bloom was too
      // loud at reading zoom as well; it was merely unbearable further out.
      const squeeze = clamp(0.35 + v.k * 0.65, 0.35, 1)
      const spread =
        r * (2.4 + glow * 3.4 + pulse * 3 + (selected ? 1.6 : hovered ? 0.9 : 0)) * squeeze
      ctx.globalCompositeOperation = p.glowMode
      // Neutral on paper. A mark that earns attention gets drawn more heavily;
      // the volume around it is a cast shadow, not more of its own pigment.
      // This was the largest coloured area anywhere in the app.
      const hue = washed(col, p)
      const a =
        (p.theme === 'dark' ? 0.34 : 0.13) * (0.35 + glow * 0.65) * (1 + pulse) * dim * squeeze
      // The gradient is built at the origin and shared — see `haloGradient`,
      // which also holds the stops and the reasoning behind their curve.
      const halo = this.haloGradient(hue, a, spread)
      ctx.translate(x, y)
      ctx.fillStyle = halo.g
      ctx.beginPath()
      ctx.arc(0, 0, halo.r, 0, TAU)
      ctx.fill()
      ctx.translate(-x, -y)
      ctx.globalCompositeOperation = 'source-over'
    }

    // The two wither terms compound: this one, and the `1 - wither * …` on the
    // fill below. At 0.42 × 0.45 a spent disc was landing at 0.19 alpha over
    // the field, which is not a mark. Both ends were raised together, because
    // fixing either alone leaves the product too low to see.
    const alpha = (0.94 + (0.56 - 0.94) * wither) * dim * p.baseAlpha

    // An objection below it stood, in reasoning: the disc's own outline, notched
    // (`canvas/notch.ts`). Never the refuted chords — this is the graph's argument.
    const spoke = Math.atan2(at.y, at.x)
    const notched = this.stoodNow?.has(n.id) ?? false
    const outline = (rr: number) => {
      if (notched) traceNotched(ctx, x, y, rr, spoke)
      else {
        ctx.beginPath()
        ctx.arc(x, y, rr, 0, TAU)
      }
    }

    if (hollow) {
      // Hollow ring — an open question, or an untested conjecture, has no centre yet.
      ctx.strokeStyle = rgba(col, alpha)
      ctx.lineWidth = Math.max(1, r * 0.42)
      outline(r * 1.12)
      ctx.stroke()
    } else {
      ctx.fillStyle = rgba(
        hot(col, (0.22 + pulse * 0.4) * (1 - wither), p),
        alpha * (1 - wither * 0.36),
      )
      outline(r)
      ctx.fill()
    }

    // A terminated path leaves a mark, not a node.
    //
    // A ring in a colour that has already drained to `dead` is close to nothing
    // on paper. Neuroanatomy has an exact convention for this: the retraction
    // bulb — the terminal swelling on a degenerating process — closed here with
    // a short transverse bar across the branch's own spoke. It says the ending
    // was *observed*, which a fade cannot, and fading already means something
    // else on this canvas (focus dimming).
    if (wither > 0) {
      const ink = p.theme === 'dark' ? col : mix(col, p.deepen, 0.4)
      const bulbR = Math.max(2.4, r * 1.4)
      // The bulb and its bar are the only marks that *say* dead end rather than
      // merely being faint, so they carry more weight than the disc they close
      // — a spent path should be findable at a glance, not just inferable from
      // an absence. A 1px hairline is what the ring was at most zooms.
      ctx.strokeStyle = rgba(ink, 0.72 * wither * dim * p.baseAlpha)
      ctx.lineWidth = Math.max(1.2, r * 0.22)
      ctx.beginPath()
      ctx.arc(x, y, bulbR, 0, TAU)
      ctx.stroke()
      const ang = Math.atan2(at.y, at.x)
      const bx = Math.cos(ang)
      const by = Math.sin(ang)
      const b0 = bulbR + Math.max(1.6, r * 0.5)
      const arm = bulbR * 0.8
      ctx.beginPath()
      ctx.moveTo(x + bx * b0 - by * arm, y + by * b0 + bx * arm)
      ctx.lineTo(x + bx * b0 + by * arm, y + by * b0 - bx * arm)
      ctx.stroke()
    }

    // A contradiction gets a cross: readable at a glance, at any zoom.
    //
    // Dark knocks the cross out from inside the disc. Paper strikes *through*
    // it and past the rim — a white knockout on a 3px disc consumed most of the
    // mark and left a red ring, which is not a cross by any reading.
    if (tension) {
      const a = r * (p.theme === 'dark' ? 0.52 : 0.84)
      // Struck in the channel's own ink rather than in flat black — a
      // near-black X on a red dot reads as a delete button, not as a
      // contradiction.
      const strike = p.theme === 'dark' ? p.deepen : mix(col, p.deepen, 0.74)
      ctx.strokeStyle = rgba(strike, (p.theme === 'dark' ? 0.9 : 0.82) * dim * (1 - wither * 0.45))
      ctx.lineWidth = Math.max(0.9, r * (p.theme === 'dark' ? 0.24 : 0.22))
      ctx.lineCap = p.theme === 'dark' ? 'butt' : 'round'
      ctx.beginPath()
      ctx.moveTo(x - a, y - a)
      ctx.lineTo(x + a, y + a)
      ctx.moveTo(x + a, y - a)
      ctx.lineTo(x - a, y + a)
      ctx.stroke()
      ctx.lineCap = 'butt'
    }

    // A refutation gets two parallel chords across the disc.
    //
    // Not a cross — that is taken, and it means the graph argued with itself. Two
    // parallel strokes are the proofreader's mark for a deletion, and they read as
    // *struck through* rather than as *cancelled*: the claim is still there, and
    // something outside the graph is standing against it. Drawn in `hot()` — a
    // highlight, going lighter on the night field and denser on paper — where the
    // contradiction cross is a knockout, so the two are distinguishable even at the
    // same hue in greyscale.
    if (refuted) {
      const a = r * 0.86
      const gapR = Math.max(0.7, r * 0.34)
      ctx.strokeStyle = rgba(hot(col, 0.62, p), 0.95 * dim * p.baseAlpha)
      ctx.lineWidth = Math.max(0.9, r * 0.2)
      ctx.lineCap = 'round'
      // Along the node's own spoke, so the pair is stable under rotation: a mark
      // whose angle depends on the layout would spin when a sibling arrives.
      const ang = Math.atan2(at.y, at.x) + Math.PI / 2
      const ux = Math.cos(ang)
      const uy = Math.sin(ang)
      const px2 = -uy
      const py2 = ux
      for (const sgn of [-1, 1]) {
        const ox = px2 * gapR * sgn * 0.5
        const oy = py2 * gapR * sgn * 0.5
        ctx.beginPath()
        ctx.moveTo(x + ox - ux * a, y + oy - uy * a)
        ctx.lineTo(x + ox + ux * a, y + oy + uy * a)
        ctx.stroke()
      }
      ctx.lineCap = 'butt'
    }

    // Fused with a branch in another exploration.
    //
    // A stub rather than a strand, because there is nowhere for a strand to go: the
    // partner has no position on this canvas and inventing one would draw a
    // relationship to a point that means nothing. Dashed and outward along the
    // node's own spoke, in the insight hue, so it reads as the same kind of
    // asserted connection the local bridge arc is — just one that leaves.
    //
    // Deliberately not silent. Silence here is indistinguishable from a local
    // partner that was pruned, which is a different fact and already reads
    // differently.
    if (n.foreignFrom && grow > 0.4) {
      const ang = Math.atan2(at.y, at.x)
      const from = Math.max(r * 2.6, r + 10)
      const to = from + 34
      ctx.strokeStyle = rgba(p.channel.insight, 0.62 * dim * p.baseAlpha)
      ctx.lineWidth = Math.max(0.8, r * 0.22)
      ctx.setLineDash([4, 5])
      ctx.beginPath()
      ctx.moveTo(x + Math.cos(ang) * from, y + Math.sin(ang) * from)
      ctx.lineTo(x + Math.cos(ang) * to, y + Math.sin(ang) * to)
      ctx.stroke()
      ctx.setLineDash([])
    }

    // Checked against sources: a crisp contour at exactly the disc's own edge.
    //
    // The quietest possible mark, and that is the point. Most nodes will never be
    // checked, and the unchecked state must not read as a deficiency — so this is a
    // sharpened edge rather than a badge, a tick, or a second ring competing with
    // the insight halo. It says "somebody looked", nothing more.
    //
    // Where the check cites a work since retracted, the same contour stands clear
    // of the disc and opens on the node's spoke (`canvas/rim.ts`): the look still
    // happened, and part of what it read was taken back. It replaces the closed
    // contour rather than joining it, so the mark stays one mark.
    const withdrawn = citesRetracted(n)
    if ((withdrawn || checkedAgainstSources(n)) && wither < 0.6) {
      ctx.strokeStyle = rgba(hot(col, p.theme === 'dark' ? 0.55 : 0.5, p), 0.7 * dim * p.baseAlpha)
      if (withdrawn) {
        ctx.lineWidth = Math.max(0.8, r * 0.16)
        ctx.lineCap = 'round'
        traceBrokenRim(ctx, x, y, r, hollow, Math.atan2(at.y, at.x))
        ctx.stroke()
        ctx.lineCap = 'butt'
      } else {
        ctx.lineWidth = Math.max(0.6, r * 0.14)
        // On a hollow node the disc's own edge is already a stroke, so the contour
        // moves outside it rather than doubling it. It follows a notch in: a
        // contour drawn round over the cut would close it.
        outline(hollow ? r * 1.42 : r)
        ctx.stroke()
      }
    }

    // An insight gets a containing ring: something closed, arrived at.
    if (insight) {
      ctx.strokeStyle = rgba(col, 0.75 * dim)
      ctx.lineWidth = Math.max(0.8, r * 0.2)
      ctx.beginPath()
      ctx.arc(x, y, r * 2.15, 0, TAU)
      ctx.stroke()
    }

    // A node the user has rewritten is marked permanently — provenance matters
    // when you are deciding whether to trust a branch.
    if (n.editedAt) {
      ctx.strokeStyle = rgba(p.accent, 0.9 * dim)
      ctx.lineWidth = Math.max(0.8, r * 0.22)
      ctx.setLineDash([2, 2.6])
      ctx.beginPath()
      ctx.arc(x, y, r * 2.8, 0, TAU)
      ctx.stroke()
      ctx.setLineDash([])
    }

    // ── FOLD ─────────────────────────────────────────────────────────────
    // A branch put down. Two chevrons on the node's own spoke, pointing
    // outward: the reasoning continues this way, and you have folded it. The
    // spoke outward of a folded node is exactly the space the fold freed, which
    // is why the mark goes there — and it sits outside every other radial mark
    // this node can carry. The *count* is in the caption: a 4px mark cannot
    // honestly be counted, so this one is bound to `collapsed` alone.
    const fk = n.foldAt ? clamp((now - n.foldAt) / (FOLD_MS / this.tempo)) : 1
    const present = this.calm ? (n.collapsed ? 1 : 0) : n.collapsed ? easeOutCubic(fk) : 1 - fk
    if (present > 0.01 && (n.collapsed ? folded > 0 : true)) {
      // The vertex, not the arm tips: each arm points back toward the node, so
      // its tip lands at base − cos(half)·arm ≈ base − 0.94r. At 3.95r that is
      // 3.0r, which clears the rewritten-by-you dashed ring at 2.8r — the
      // outermost mark this node can otherwise carry.
      const base = Math.max(r * 3.95, r + 15)
      const ang = Math.atan2(at.y, at.x)
      const ux = Math.cos(ang)
      const uy = Math.sin(ang)
      const arm = Math.max(2.6, r * 1.15)
      const step = Math.max(2.6, r * 1.1)
      const half = 0.62
      ctx.lineWidth = Math.max(1, r * 0.26)
      ctx.lineCap = 'round'
      ctx.lineJoin = 'round'
      for (const i of [0, 1]) {
        // Sweeping inward as the fold lands; outward again as it releases.
        const d = (base + i * step) * (1 + (1 - present) * 0.9)
        const ax = x + ux * d
        const ay = y + uy * d
        ctx.strokeStyle = rgba(col, 0.8 * (i ? 0.62 : 1) * dim * present * p.baseAlpha)
        ctx.beginPath()
        for (const sgn of [-1, 1]) {
          const c = Math.cos(half * sgn)
          const sn = Math.sin(half * sgn)
          ctx.moveTo(ax, ay)
          ctx.lineTo(ax + (-ux * c + uy * sn) * arm, ay + (-ux * sn - uy * c) * arm)
        }
        ctx.stroke()
      }
      ctx.lineCap = 'butt'
      ctx.lineJoin = 'miter'
    }

    if (selected || hovered) {
      ctx.strokeStyle = rgba(p.text1, selected ? 0.7 : hoverRingAlpha(p))
      ctx.lineWidth = 1
      ctx.beginPath()
      ctx.arc(x, y, r + (selected ? 6 : 4.5), 0, TAU)
      ctx.stroke()
    }

    ctx.restore()
  }

  /* ── frame ────────────────────────────────────────────────────────── */

  draw(f: FrameInput, now: number, dt = 16) {
    const { ctx } = this
    this.signals = f.filters.particles
    this.budget = 110
    this.tempo = f.tempo
    // The node table for this frame. The strand pass needs to ask whether its own
    // parent carries a recorded result, and building any per-strand lookup for that
    // would be an allocation per strand per frame.
    this.nodesNow = f.session.nodes
    // Memoised on the identity of `nodes`, so this is a map lookup on every frame
    // but the settle frames after an arrival, where it is one O(n) walk.
    this.taintNow = taintMap(f.session)
    this.evidenceStaleNow = evidenceStaleMap(f.session)
    this.stoodNow = stoodAgainst(f.session)
    motion.step(f.session.nodes, f.session.order, dt)

    // Did anything a caption or the minimap draws MOVE this frame? The canvas
    // itself never rests — the seed breathes, signals travel — but positions
    // change only when the camera, the eased layout, the revision, the
    // timeline or the stage does, and that is what the overlays key on to skip
    // the rest. Published on `screen` with the camera; see finding 6.
    {
      const v = f.view
      const moved =
        motion.moving ||
        f.rev !== this.lastRev ||
        v.k !== this.lastK ||
        v.x !== this.lastX ||
        v.y !== this.lastY ||
        f.playhead !== this.lastPlayhead ||
        f.playheadAt !== this.lastPlayheadAt ||
        this.w !== this.lastW ||
        this.h !== this.lastH
      this.lastRev = f.rev
      this.lastK = v.k
      this.lastX = v.x
      this.lastY = v.y
      this.lastPlayhead = f.playhead
      this.lastPlayheadAt = f.playheadAt
      this.lastW = this.w
      this.lastH = this.h
      if (moved) screen.frame++
    }

    // ── Replay clock ─────────────────────────────────────────────────────
    // While the timeline is engaged, rewrite each node's birth so the strand
    // most recently revealed is growing *now* and the ones before it are
    // correspondingly older. Without this the scrubber only toggles
    // visibility, and replay shows you the answer instead of the thinking.
    this.birthAt.clear()
    if (f.playhead !== null) {
      const step = BASE.beat / f.tempo
      const head = f.playhead - 1
      for (let i = 0; i <= head && i < f.session.order.length; i++) {
        this.birthAt.set(f.session.order[i]!, f.playheadAt - (head - i) * step)
      }
    }

    // Publish this frame's growth clock for the text overlay. See growth.ts.
    growth.bornAt = (id, birth) => this.birthAt.get(id) ?? birth
    growth.landsAfter = this.phases(0).nodeAt
    growth.calm = this.calm

    ctx.save()
    ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0)
    ctx.clearRect(0, 0, this.w, this.h)

    this.background(f.palette, f.view, f.rings)

    const { session, view: v, palette: p, visible, focus, guideKeep } = f
    const active = new Set(f.activeTargets)
    const selected = f.selectedId
    const hover = f.hoverId

    // Lineage of the current selection reads brighter; it is the thread the
    // user is actually holding.
    const litPath = new Set<string>()
    if (selected) {
      let cur: string | null = selected
      const guard = new Set<string>()
      while (cur && !guard.has(cur)) {
        guard.add(cur)
        litPath.add(cur)
        cur = session.nodes[cur]?.parentId ?? null
      }
    }

    // A claim a rethink has replaced stays drawn, receded, so the new heads beside
    // it read as the current version (`priorVersionIds`) — except the one being
    // read, which is the side of the comparison somebody opened.
    const prior = priorVersionIds(session)
    const dimFor = (id: string) => {
      const ink = prior.has(id) && id !== selected ? PRIOR_VERSION_INK : 1
      if (focus && !focus.has(id)) return (p.theme === 'dark' ? 0.17 : 0.22) * ink
      if (guideKeep && !guideKeep.has(id)) return (p.theme === 'dark' ? 0.12 : 0.18) * ink
      return ink
    }
    // How long a strand takes to grow, as `phases` draws it: the full choreography,
    // or reduced motion's third of a second. Every window below that follows a
    // landing is measured in it, so none outlives the frames the loop keeps.
    const grownMs = this.phases(0).grown
    const hotFor = (n: ReasonNode) => {
      const recent = this.pulseOf(n, now)
      const born = clamp(1 - (now - this.born(n)) / (grownMs * 1.1))
      let h = Math.max(recent, born * 0.75)
      if (active.has(n.id) || (n.parentId && active.has(n.parentId))) h = Math.max(h, 0.85)
      if (litPath.has(n.id)) h = Math.max(h, 0.42)
      if (n.id === hover) h = Math.max(h, 0.55)
      return clamp(h)
    }

    // Two passes so live reasoning never sits behind a dead end.
    //
    // The same walk notes whether any strand is still young enough to be
    // animating without moving: its caption fades up on touchdown, so the
    // overlay may not skip those frames. Generous — one and a half growths —
    // because the caption's own fade runs past the node's landing. In `grownMs`,
    // so under reduced motion it is half a second, not the five the full growth
    // takes: that was five seconds of every frame after each landing and each
    // replay step, on the one setting that asks for nothing to move (D191).
    const youngFor = grownMs * 1.5
    let young = false
    const strands: ReasonNode[] = []
    for (const id of session.order) {
      const n = session.nodes[id]
      if (!n || !visible.has(id) || n.parentId === null) continue
      if (!visible.has(n.parentId)) continue
      strands.push(n)
      if (!young && now - this.born(n) < youngFor) young = true
    }
    screen.animating = young || f.playhead !== null
    let settling = young
    // Dead ends sit behind live reasoning — but only once they have actually
    // withered. A dead end mid-growth is the thing you are watching, and
    // burying it under its siblings would hide the moment it dies.
    const spent = (n: ReasonNode) =>
      n.terminal && now - this.born(n) > (GROW_TOTAL + 1200) / this.tempo
    strands.sort((a, b) => {
      const da = spent(a) ? 0 : 1
      const db = spent(b) ? 0 : 1
      if (da !== db) return da - db
      return drawnRatingsOf(a).confidence - drawnRatingsOf(b).confidence
    })

    // Every strand, signal and bridge stays out of the seed's clear field: the
    // title is bare on empty ground and the branches stem from its edge.
    const rootNode = session.order.map((id) => session.nodes[id]).find((n) => n?.parentId === null)
    const clear = rootNode ? this.seedClear(rootNode, v) : null
    ctx.save()
    if (clear) {
      ctx.beginPath()
      ctx.rect(-1e5, -1e5, 2e5, 2e5)
      ctx.ellipse(clear.x, clear.y, clear.rx, clear.ry, 0, 0, TAU)
      ctx.clip('evenodd')
    }

    for (const n of strands) {
      const parent = session.nodes[n.parentId!]!
      this.drawStrand(n, parent, p, v, now, f.rev, dimFor(n.id), hotFor(n))
    }

    this.dispatch(f, now)

    for (const n of strands) {
      if (!n.bridgeFrom) continue
      const other = n.bridgeFrom.find((b) => b !== n.parentId)
      const o = other ? session.nodes[other] : undefined
      if (o && visible.has(o.id)) this.drawBridge(n, o, p, v, now, dimFor(n.id))
    }
    ctx.restore()

    for (const id of session.order) {
      const n = session.nodes[id]
      if (!n || !visible.has(id)) continue
      if (!settling && this.transient(n, now)) settling = true
      if (n.parentId === null) {
        const kids = strands.filter((c) => c.parentId === n.id)
        this.drawRoot(n, kids, p, v, now, f.busy, n.id === selected, n.id === hover)
      } else {
        this.drawNode(
          n,
          p,
          v,
          now,
          dimFor(id),
          id === selected,
          id === hover,
          f.folded.get(id) ?? 0,
        )
      }
    }

    ctx.restore()
    this.settling = settling
    this.publish(f)
  }

  /**
   * Hand the label layer this frame's camera. See screen.ts.
   *
   * The overlay used to compute this itself, which was correct and also a second
   * copy of the statement "the projection is an affine transform" — true here and
   * false in the spatial view. Publishing it means the overlay places captions from
   * whichever camera actually painted, and neither renderer has to know the other
   * exists.
   */
  private publish(f: FrameInput) {
    const v = f.view
    const nodes = f.session.nodes
    screen.mode = '2d'
    screen.originX = v.x
    screen.originY = v.y
    // The clear field `drawRoot` and the strand clip use, so the overlay pushes
    // first-ring captions out of the same circle the strands keep out of.
    const root = f.session.order.map((id) => nodes[id]).find((n) => n?.parentId === null)
    screen.orbR = root ? this.seedClear(root, v).rx : ROOT_CLEAR * v.k
    screen.zoom = v.k
    screen.place = (id: string) => {
      const n = nodes[id]
      if (!n) return { x: 0, y: 0, ux: 1, uy: 0, s: v.k, z: 0, on: false }
      const at = motion.at(n)
      const x = v.x + at.x * v.k
      const y = v.y + at.y * v.k
      // The world angle, which survives an affine transform unchanged — the very
      // fact the spatial view cannot rely on.
      const len = Math.hypot(at.x, at.y)
      return {
        x,
        y,
        ux: len > 0.001 ? at.x / len : 1,
        uy: len > 0.001 ? at.y / len : 0,
        s: v.k,
        z: 0,
        on: true,
      }
    }
  }

  /* ── interaction ──────────────────────────────────────────────────── */

  /** Nearest node within a generous screen-space radius, or null. `floor` is the
   *  smallest reach a node gets — wider for a finger's tap (`tapReach`). */
  hitTest(f: FrameInput, sx: number, sy: number, floor = 13): string | null {
    return nodeAt(f, sx, sy, floor)
  }

  /** Nearest strand within a few pixels — selects the child it terminates at. */
  hitStrand(f: FrameInput, sx: number, sy: number): string | null {
    const { session, view: v, visible } = f
    const wx = (sx - v.x) / v.k
    const wy = (sy - v.y) / v.k
    const tol = 7 / v.k
    let best: string | null = null
    let bestD = Infinity
    for (const id of session.order) {
      const n = session.nodes[id]
      if (!n || !visible.has(id) || n.parentId === null) continue
      const g = this.geom.get(id)
      if (!g) continue
      const d = distToPolyline(g.pts, wx, wy)
      if (d < tol && d < bestD) {
        bestD = d
        best = id
      }
    }
    return best
  }
}

/**
 * The renderer's node hit test, outside the class so it can be read without a
 * canvas. Nearest wins, so a wider `floor` only reaches dots nothing else is
 * nearer to.
 */
export function nodeAt(f: FrameInput, sx: number, sy: number, floor = 13): string | null {
  const { session, view: v, visible } = f
  let best: string | null = null
  let bestD = Infinity
  for (const id of session.order) {
    const n = session.nodes[id]
    if (!n || !visible.has(id)) continue
    const at = motion.at(n)
    const x = v.x + at.x * v.k
    const y = v.y + at.y * v.k
    const d = Math.hypot(sx - x, sy - y)
    // Root has a large target; leaves need a forgiving one at low zoom.
    // A stub is the only way to reach a whole branch, so it gets a slightly
    // more generous target than an ordinary node.
    const reach =
      n.parentId === null ? 108 * v.k : Math.max(floor, 10 * v.k) * (n.collapsed ? 1.35 : 1)
    if (d < reach && d < bestD) {
      bestD = d
      best = id
    }
  }
  return best
}
